import React, { useState } from "react";
import { assetUrl, cleanArtifactFileName } from "../../config/runtime";
import "./filecard.css";

interface FileCardProps {
  fileName: string;
  fileUrl: string;
}

const getFileIcon = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  switch (ext) {
    case "py":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="file-card-icon file-card-icon--py">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      );
    case "csv":
    case "xlsx":
    case "xls":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="file-card-icon file-card-icon--csv">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M8 13h8M8 17h8" />
        </svg>
      );
    case "json":
    case "txt":
    case "md":
    case "log":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="file-card-icon file-card-icon--txt">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="file-card-icon">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <polyline points="13 2 13 9 20 9" />
        </svg>
      );
  }
};

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CopyLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const FileCard: React.FC<FileCardProps> = ({ fileName, fileUrl }) => {
  const [copied, setCopied] = useState(false);
  const safeFileName = cleanArtifactFileName(fileName, fileUrl);
  const ext = safeFileName.split(".").pop()?.toUpperCase() || "FILE";
  // 兜底绝对化：后端产出的是相对路径 /uploads/xxx，调用方多数已经过 assetUrl，
  // 这里再过一次（幂等）避免漏改的调用方把 fetch 打到前端 devServer 上 404。
  const resolvedUrl = assetUrl(fileUrl);

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      navigator.clipboard?.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(resolvedUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = safeFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(resolvedUrl, "_blank");
    }
  };

  return (
    <div className="file-card">
      <div className="file-card-main" onClick={handleDownload} title={`点击下载 ${safeFileName}`}>
        <div className="file-card-icon-wrap">{getFileIcon(safeFileName)}</div>
        <div className="file-card-info">
          <span className="file-card-name" title={safeFileName}>
            {safeFileName}
          </span>
          <span className="file-card-meta">{ext} 离线工作区产物</span>
        </div>
      </div>

      <div className="file-card-actions">
        <button
          type="button"
          className="file-card-btn file-card-btn--download"
          onClick={handleDownload}
          title="下载文件"
        >
          <DownloadIcon />
          <span>下载</span>
        </button>
        <button
          type="button"
          className="file-card-btn file-card-btn--copy"
          onClick={handleCopyLink}
          title="复制链接"
        >
          <CopyLinkIcon />
          <span>{copied ? "已复制" : "复制链接"}</span>
        </button>
      </div>
    </div>
  );
};

export default FileCard;
